import dayjs from "dayjs";
import { inflate } from "pako";
import { decodeBase64ToBytes } from "../services/utils";
import { Duration } from "../utils/dayjs";
import { Interaction, InteractionData } from "@auvious/interaction";
import { IInteractionMetrics, IRecorderInfo, RecorderStateEnum } from ".";
import {
  InteractionMetricEnum,
  ConversationTypeEnum,
  EndpointTypeEnum,
  IInteractionMetricConference,
  IConferenceEvent
} from "../core-ui.enums";

export class InteractionMetrics implements IInteractionMetrics {
  private _data: InteractionData = {};
  private _id: string;
  private _organizationId: string;
  private _duration: Duration;
  private _type: ConversationTypeEnum;
  private _mediaRecorderInfo: IRecorderInfo;
  private _cobrowseRecorderInfo: IRecorderInfo;
  private _cobrowseRequestType: EndpointTypeEnum;
  private _createdBy: string;
  private _conferences: { [id: string]: IInteractionMetricConference };
  private _conferenceEvents: { [id: string]: IConferenceEvent[] };
  private _callHoldDuration: Duration;

  constructor(_interaction: Interaction) {
    const { id, organizationId, data, type, createdBy } = _interaction;
    this._id = id;
    this._organizationId = organizationId;
    this._data = data;
    this._createdBy = createdBy;
    this._type = type as ConversationTypeEnum;
    this._conferences = this._data[InteractionMetricEnum.conferences];
    this._conferenceEvents = {};

    if (this._conferences) {
      for (const [confId, conf] of Object.entries(this._conferences)) {
        if (conf?.eventsJsonGzBase64) {
          try {
            const bytes = decodeBase64ToBytes(conf.eventsJsonGzBase64);
            const json = inflate(bytes, { to: "string" });
            conf.events = JSON.parse(json);
            this._conferenceEvents[confId] = conf.events;
          } catch {
            conf.events = null;
          }
        }
      }
    }

    if (this._conferences) {
      this._duration = Object.values(this._conferences).reduce(
        (total, data) => total.add(data.duration),
        dayjs.duration(0)
      );
      this._callHoldDuration = Object.values(this._conferences).reduce(
        (total, data) => total.add(data.on_hold_duration),
        dayjs.duration(0)
      );
    } else if (this.getCallStartAt() && this.getCallEndAt()) {
      this._duration = dayjs.duration(
        dayjs(this.getCallEndAt()).diff(dayjs(this.getCallStartAt()))
      );
    }
    if (this._data?.[InteractionMetricEnum.recorderInstanceId]) {
      this._mediaRecorderInfo = {
        instanceId: this._data[InteractionMetricEnum.recorderInstanceId],
        recorderId: this._data[InteractionMetricEnum.recorderId],
        conversationId: this._id,
        state: RecorderStateEnum.stopped
      };
    }
    if (this._data?.[InteractionMetricEnum.cobrowseRecorderInstanceId]) {
      this._cobrowseRecorderInfo = {
        instanceId:
          this._data[InteractionMetricEnum.cobrowseRecorderInstanceId],
        recorderId: this._data[InteractionMetricEnum.cobrowseRecorderId],
        conversationId: this._id,
        state: RecorderStateEnum.stopped
      };
    }
    this._cobrowseRequestType =
      this._data?.[InteractionMetricEnum.cobrowseRequestType] ||
      EndpointTypeEnum.coBrowse;
  }
  getId() {
    return this._id;
  }
  getCallType() {
    return this._type;
  }
  getCallOrigin() {
    return this._data?.[InteractionMetricEnum.callOrigin];
  }
  getCallDestination() {
    return this._data?.[InteractionMetricEnum.callDestination];
  }
  getCallOriginMode() {
    return this._data?.[InteractionMetricEnum.callOriginMode];
  }
  getCallStartAt() {
    return this._data?.[InteractionMetricEnum.callStartAt];
  }
  getCallEndAt() {
    return this._data?.[InteractionMetricEnum.callEndAt];
  }
  getCallDuration() {
    return this._duration;
  }
  getCallHoldDuration(): Duration {
    return this._callHoldDuration;
  }
  getCallRecorderInfo(): IRecorderInfo {
    return this._mediaRecorderInfo;
  }
  getCobrowseRecorderInfo(): IRecorderInfo {
    return this._cobrowseRecorderInfo;
  }
  getCobrowseRequestType(): EndpointTypeEnum {
    return this._cobrowseRequestType;
  }
  getOriginUrl(): string {
    return this._data?.[InteractionMetricEnum.originUrl];
  }
  getCreatedBy(): string {
    return this._createdBy;
  }
  getCustomerDisplayName(): string {
    return this._data?.[InteractionMetricEnum.customerDisplayName];
  }
  getCustomerMetadata(): { [key: string]: any } {
    return this._data?.[InteractionMetricEnum.customerMetadata];
  }
  getCustomerId(): string {
    return this._data?.[InteractionMetricEnum.customerId];
  }
  setMetric(key: InteractionMetricEnum, value: any) {
    this._data = {
      ...this._data,
      [key]: value
    };
    // recalculate duration
    if (
      [
        InteractionMetricEnum.callEndAt,
        InteractionMetricEnum.callStartAt
      ].includes(key)
    ) {
      this._duration = dayjs.duration(
        dayjs(this.getCallEndAt()).diff(dayjs(this.getCallStartAt()))
      );
    }
  }
  getMetric(key: InteractionMetricEnum) {
    return this._data[key];
  }
  getMetrics(): InteractionData {
    return this._data;
  }
  getTermsAcceptedAt(): Date {
    return !!this._data?.[InteractionMetricEnum.termsAcceptedAt]
      ? new Date(this._data[InteractionMetricEnum.termsAcceptedAt])
      : null;
  }
  getTermsAcceptedSnapshot(): string {
    return this._data?.[InteractionMetricEnum.termsAcceptedSnapshot];
  }
  getIntegrationInteractionAvailable(): boolean {
    return (
      this._data?.[InteractionMetricEnum.integrationInteractionAvailable] ??
      true
    );
  }
  getParentInteraction(): string {
    return this._data.parentInteraction;
  }
  getChildInteractions(): { id: string; createdAt: string }[] {
    return this._data.childInteractions;
  }
  getConferenceMap(): { [id: string]: IInteractionMetricConference } {
    return this._conferences;
  }
  getConferenceEvents(conferenceId: string): IConferenceEvent[] | null {
    return this._conferenceEvents?.[conferenceId] ?? null;
  }
}
